import { useCallback, useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  collection, 
  getDocs, 
  doc, 
  updateDoc, 
  getDoc,
  setDoc,
  addDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';

// Helper function to determine what fields have changed
const getChangedFields = (original, updates) => {
  const changedFields = [];
  
  // Check direct properties
  Object.keys(updates).forEach(key => {
    // Special handling for notes array to compare content instead of references
    if (key === 'notes' && Array.isArray(original[key]) && Array.isArray(updates[key])) {
      // Extract just the content from notes
      const originalContents = original[key].map(note => note.content).filter(Boolean);
      const updatedContents = updates[key].map(note => note.content).filter(Boolean);
      
      // Check if content is different (ignoring other properties like timestamps)
      const contentChanged = JSON.stringify(originalContents) !== JSON.stringify(updatedContents);
      console.log('⭐ Notes comparison:', { 
        originalContents, 
        updatedContents, 
        contentChanged 
      });
      
      if (contentChanged) {
        changedFields.push(key);
        console.log(`⭐ Notes changed: content is different`);
      } else {
        console.log(`⭐ Notes unchanged: content is the same, ignoring metadata changes`);
      }
    }
    // Normal handling for other fields
    else if (key !== 'categoryData' && JSON.stringify(original[key]) !== JSON.stringify(updates[key])) {
      changedFields.push(key);
    }
  });
  
  // Check categoryData
  if (updates.categoryData && original.categoryData) {
    Object.keys(updates.categoryData).forEach(key => {
      const originalValue = original.categoryData[key]?.value;
      const updatedValue = updates.categoryData[key]?.value;
      
      if (JSON.stringify(originalValue) !== JSON.stringify(updatedValue)) {
        // Extract field name without 'category_' prefix for the changedFields array
        const fieldName = key.startsWith('category_') ? key.substring(9) : key;
        changedFields.push(fieldName);
      }
    });
  }
  
  return changedFields;
};

// Main API hook for Firebase operations
export const useApi = () => {
  const auth = useAuth();
  const user = auth?.user;

  // Cache state
  const [hotelsCache, setHotelsCache] = useState(null);
  const [categoriesCache, setCategoriesCache] = useState(null);
  const lastFetchTime = useRef({
    hotels: null,
    categories: null
  });

  // Cache duration in milliseconds (5 minutes)
  const CACHE_DURATION = 5 * 60 * 1000;

  // Helper to check if cache is valid
  const isCacheValid = useCallback((cacheType) => {
    const lastFetch = lastFetchTime.current[cacheType];
    return lastFetch && (Date.now() - lastFetch) < CACHE_DURATION;
  }, [CACHE_DURATION]);

  // Memoize getHotels with cache
  const getHotels = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh && hotelsCache && isCacheValid('hotels')) {
      return hotelsCache;
    }

    const hotelsRef = collection(db, 'hotels');
    const snapshot = await getDocs(hotelsRef);
    const hotels = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    setHotelsCache(hotels);
    lastFetchTime.current.hotels = Date.now();
    return hotels;
  }, [hotelsCache, isCacheValid]);

  // Memoize updateHotel with cache update
  const updateHotel = useCallback(async (id, data) => {
    console.log('⭐ useApi.updateHotel called:', { id, data });
    
    try {
      // Get the current hotel data first for change detection
      const hotelRef = doc(db, 'hotels', id);
      const hotelDoc = await getDoc(hotelRef);
      
      if (!hotelDoc.exists()) {
        throw new Error('Hotel not found');
      }
      
      const currentHotel = hotelDoc.data();
      console.log('⭐ Current hotel data retrieved');
      
      // Update the hotel in Firestore
      await updateDoc(hotelRef, data);
      console.log('⭐ Hotel document updated successfully');
      
      // Update local cache if it exists
      if (hotelsCache) {
        const updatedHotels = hotelsCache.map(hotel => 
          hotel.id === id ? { ...hotel, ...data } : hotel
        );
        setHotelsCache(updatedHotels);
      }
      
      // Determine what fields have changed
      const changedFields = getChangedFields(currentHotel, data);
      console.log('⭐ Detected changed fields:', changedFields);
      
      // Enhanced data structure to track what values changed
      const changesWithValues = [];
      
      // Check direct properties
      Object.keys(data).forEach(key => {
        // Special handling for notes array
        if (key === 'notes' && Array.isArray(currentHotel[key]) && Array.isArray(data[key])) {
          // Extract just the content from notes
          const originalContents = currentHotel[key].map(note => note.content).filter(Boolean);
          const updatedContents = data[key].map(note => note.content).filter(Boolean);
          
          // Check if content is different (ignoring other properties like timestamps)
          const contentChanged = JSON.stringify(originalContents) !== JSON.stringify(updatedContents);
          
          if (contentChanged) {
            changesWithValues.push({
              field: key,
              from: currentHotel[key],
              to: data[key]
            });
          }
        }
        // Normal handling for other fields
        else if (key !== 'categoryData' && JSON.stringify(currentHotel[key]) !== JSON.stringify(data[key])) {
          changesWithValues.push({
            field: key,
            from: currentHotel[key],
            to: data[key]
          });
        }
      });
      
      // Check categoryData
      if (data.categoryData && currentHotel.categoryData) {
        Object.keys(data.categoryData).forEach(key => {
          const originalValue = currentHotel.categoryData[key]?.value;
          const updatedValue = data.categoryData[key]?.value;
          
          if (JSON.stringify(originalValue) !== JSON.stringify(updatedValue)) {
            // Extract field name without 'category_' prefix
            const fieldName = key.startsWith('category_') ? key.substring(9) : key;
            changesWithValues.push({
              field: fieldName,
              from: originalValue,
              to: updatedValue
            });
          }
        });
      }
      
      // Check if we have any changed fields to log
      if (changedFields && changedFields.length > 0) {
        console.log('⭐ Creating activity log entry for hotel update');
        
        // Create an activity log entry
        try {
          const logData = {
            timestamp: serverTimestamp(),
            user: {
              id: user.uid,
              email: user.email,
              role: user.role || 'unknown'
            },
            action: 'HOTEL_UPDATED',
            entityId: id,
            entityType: 'hotel',
            details: {
              hotelName: currentHotel.categoryData?.category_name?.value || 'Unknown Hotel',
              hotelPropertyNumber: currentHotel.categoryData?.category_propertynumber?.value || id,
              changedFields: changedFields,
              changes: changesWithValues
            }
          };
          
          const logRef = await addDoc(collection(db, 'activityLogs'), logData);
          console.log('⭐ Activity log created successfully with ID:', logRef.id);
        } catch (logError) {
          console.error('⭐ Failed to create activity log:', logError);
          // Don't fail the operation if logging fails
        }
      } else {
        console.log('⭐ No changes detected to log');
      }
      
      return { success: true };
    } catch (error) {
      console.error('⭐ Error in updateHotel:', error);
      throw error;
    }
  }, [hotelsCache, user]);

  // Memoize getCategories with cache
  const getCategories = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh && categoriesCache && isCacheValid('categories')) {
      return categoriesCache;
    }

    const categoriesRef = doc(db, 'metadata', 'categories');
    const snapshot = await getDoc(categoriesRef);
    const categories = snapshot.exists() ? snapshot.data().categories : [];

    setCategoriesCache(categories);
    lastFetchTime.current.categories = Date.now();
    return categories;
  }, [categoriesCache, isCacheValid]);

  // Memoize updateCategories with cache update
  const updateCategories = useCallback(async (categories) => {
    const categoriesRef = doc(db, 'metadata', 'categories');
    await setDoc(categoriesRef, { categories }, { merge: true });
    
    setCategoriesCache(categories);
    lastFetchTime.current.categories = Date.now();
    
    return { success: true };
  }, []);

  // User profile functions
  const getUserProfile = useCallback(async () => {
    if (!user) return null;
    const userRef = doc(db, 'users', user.uid);
    const snapshot = await getDoc(userRef);
    return snapshot.exists() ? snapshot.data() : null;
  }, [user]);

  const updateUserProfile = useCallback(async (data) => {
    if (!user) return { success: false };
    const userRef = doc(db, 'users', user.uid);
    await setDoc(userRef, data, { merge: true });
    return { success: true };
  }, [user]);

  return {
    getHotels,
    updateHotel,
    getCategories,
    updateCategories,
    getUserProfile,
    updateUserProfile
  };
}; 